import {Button, Dropdown, Form, Input, Menu, notification, Popover, Tooltip, Upload} from 'antd';
import {UploadFile} from 'antd/lib/upload/interface';
import classnames from 'classnames';
import {ErrorMessage} from 'formik';
import React, {useCallback, useEffect, useRef, useState, Dispatch, SetStateAction} from 'react';
import {CloseCircleOutlined, FileOutlined, PaperClipOutlined, PlusOutlined} from '@ant-design/icons';
import {findIconDefinition} from '@fortawesome/fontawesome-svg-core';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';

import {Template} from '../../../../../types/src/';
import {
  ConfirmationModal,
  IconWrapper,
  InfoIcon,
  MessageLengthCount,
  STRINGS,
  TemplatesMenu,
} from '../../../../../ui/src/index';
import {ReactComponent as AiIcon} from '../../../assets/svg/ai_icon.svg';
import {CONSTANTS, formatAttachmentName} from '../../../utils';
import {AdminFeatureIcon} from '../../icons';

import './styles.scss';

type InsertButton = {
  value: string;
  label: string;
};

interface MessageTextAreaProps {
  label?: string | React.ReactNode;
  value: string;
  setValue: (value: string) => void;
  insertTexts: InsertButton[];
  placeholder: string;
  errors?: any;
  touched?: any;
  name: string;
  additionalData?: string | React.ReactNode;
  showAttachment?: boolean;
  attachment?: any;
  setAttachment?: (value: any) => void;
  disabled?: boolean;
  clientAttachments?: any;
  deleteClientAttachment?: (attachment_id: string | number) => void;
  display?: boolean;
  dataTestId?: string;
  isAdmin?: boolean;
  templates?: any;
  defaultTemplate?: any;
  className?: string;
  showWhitelabelSignature?: boolean;
  onUpgradePlan?: () => Promise<any>;
  createTemplate?: (message?: string) => void;
  onSuggestMessageTemplate?: (params: any) => void;
  tooltip?: string;
  maxLength?: number;
  client?: any;
}

// fix- if you click on template in one text area and then on another template in the next text area - prev open template closes
let activeDropdown: Dispatch<SetStateAction<boolean>> | null = null;

export const MessageTextAreaV2 = ({
  label,
  value,
  setValue,
  insertTexts,
  placeholder,
  errors,
  touched,
  name,
  additionalData,
  showAttachment,
  attachment,
  setAttachment,
  disabled,
  clientAttachments,
  deleteClientAttachment,
  display = true,
  dataTestId,
  isAdmin,
  templates,
  defaultTemplate,
  className,
  showWhitelabelSignature,
  createTemplate,
  onUpgradePlan,
  onSuggestMessageTemplate,
  tooltip,
  maxLength,
  client,
}: MessageTextAreaProps) => {
  const elementRef = useRef(null);
  const backdropRef = useRef(null);
  const highlightsRef = useRef(null);
  const [searchText, setSearchText] = useState('');
  const [templatesDropdownVisible, setTemplatesDropdownVisible] = useState(false);
  const [showUpgradeConfirmation, setShowUpgradeConfirmation] = useState(false);

  useEffect(() => {
    return () => {
      if (activeDropdown === setTemplatesDropdownVisible) {
        activeDropdown = null;
      }
    };
  }, []);

  const applyHighlights = useCallback(
    (text = '') => {
      let result = text;
      result = result?.replace(/\n$/g, '\n\n') ?? '';

      const allVariables = [
        ...insertTexts,
        {
          value: '{receiver.company_name}',
        },
        // {
        //   value: '{receiver.most_recent_past_company}',
        // },
        // {
        //   value: '{receiver.company_industry}',
        // },
        // {
        //   value: '{receiver.company_region}',
        // },
        // {
        //   value: '{receiver.receiver_region}',
        // },
      ];

      allVariables.forEach((replaceKey: any) => {
        if (result.includes(replaceKey.key) && replaceKey.editable) {
          const splittedMessage = splitByReplaceKey(result, replaceKey.key);
          const highlightedMessage = `${splittedMessage.shift()}<mark>${replaceKey.key}`;

          result = splittedMessage.reduce((completeMessage, chunkMessage, currIndex) => {
            const firstCurlyBraceIndex = chunkMessage.indexOf('}') + 1;
            const companyNameFallback = chunkMessage.substring(0, firstCurlyBraceIndex);
            const afterCompanyNameFallback = chunkMessage.substring(firstCurlyBraceIndex);

            const highlightedChunk = `${completeMessage}${companyNameFallback}</mark>${afterCompanyNameFallback}`;

            const isLastElement = currIndex === splittedMessage.length - 1;

            if (isLastElement) {
              return highlightedChunk;
            }

            return `${highlightedChunk}<mark>${replaceKey.key}`;
          }, highlightedMessage);
        } else {
          result = result.split(replaceKey.value).join(`<mark>${replaceKey.value}</mark>`);
        }
      });

      return result;
    },
    [insertTexts]
  );

  const handleInput = useCallback(() => {
    const highlightedText = applyHighlights(value);
    highlightsRef.current.innerHTML = highlightedText;
  }, [applyHighlights, value]);

  useEffect(() => {
    handleInput();
    if (elementRef.current && elementRef.current.resizableTextArea && elementRef.current.resizableTextArea.textArea) {
      elementRef.current.resizableTextArea.textArea.addEventListener('scroll', handleScroll);
      return () => {
        elementRef.current?.resizableTextArea?.textArea?.removeEventListener('scroll', handleScroll);
      };
    }
  }, [elementRef, handleInput]);

  useEffect(() => {
    handleInput();
  }, [value, handleInput]);

  const handleScroll = () => {
    if (
      elementRef.current &&
      elementRef.current.resizableTextArea &&
      elementRef.current.resizableTextArea.textArea &&
      backdropRef.current
    ) {
      backdropRef.current.scrollTop = elementRef.current.resizableTextArea.textArea.scrollTop;
      backdropRef.current.scrollLeft = elementRef.current.resizableTextArea.textArea.scrollLeft;
    }
  };

  const splitByReplaceKey = (text, key) => {
    return text.split(key);
  };
  const insertText = (item: any) => {
    const companyFallback = CONSTANTS.COMPANY_VARIABLES_FALLBACK[item.fallback];
    if (companyFallback) {
      const desc = (
        <div>
          you chose the variable <strong>{item.value}</strong>. This variable <strong>receiver.{item.fallback}</strong>{' '}
          is not always present for all receivers. When it is not found, such value will be replaced with the editable
          default text provided after the OR (in this example <strong>{companyFallback}</strong>)
        </div>
      );

      notification.warning({
        message: desc,
        duration: 10,
      });
    }
    if (elementRef.current && elementRef.current.resizableTextArea.textArea) {
      const textArea = elementRef.current.resizableTextArea.textArea;
      const {selectionStart = 0} = textArea;
      const currentValue = value ?? '';

      const newValue = currentValue.slice(0, selectionStart) + item.value + currentValue.slice(selectionStart);
      setValue(newValue);

      setTimeout(() => {
        textArea.focus();
        textArea.setSelectionRange(selectionStart + item.value.length, selectionStart + item.value.length);
      }, 0);
    }
  };

  const handleFileClick = async (file: any, setAttachment: any) => {
    setAttachment(file);
  };

  const isAttachmentString = typeof attachment === 'string';
  let attachmentName = isAttachmentString
    ? attachment
    : attachment
    ? attachment.attachment_name || attachment.name
    : null;
  attachmentName = formatAttachmentName(attachmentName);
  const attachmentElement = showAttachment ? (
    attachmentName ? (
      <Button onClick={() => (setAttachment ? setAttachment('') : null)}>
        <FileOutlined />
        {attachmentName}
        <CloseCircleOutlined />
      </Button>
    ) : (
      <Popover
        placement="bottomRight"
        title={
          <div className="message-text-area__attachment-title">
            <span>Select a file</span>
            <span className="message-text-area__attachment-size">[Max: 20 MB]</span>
          </div>
        }
        getPopupContainer={(triggerNode: any) => triggerNode.parentNode}
        content={
          <div className="message-text-area__attachment-content">
            <Upload
              multiple={false}
              showUploadList={true}
              fileList={clientAttachments?.list?.data.map(({id, attachment_name}) => {
                return {
                  uid: id,
                  id,
                  name: attachment_name,
                };
              })}
              accept=".csv,.xlsx,.doc,.pdf,.txt,.html,.htm,.jpeg,.jpg,.png"
              beforeUpload={(file: any) => {
                if (setAttachment) {
                  setAttachment(file);
                }
                return false;
              }}
              onRemove={file => {
                deleteClientAttachment(file.uid);
              }}
              onPreview={(file: UploadFile) => handleFileClick(file, setAttachment)}
            >
              <Tooltip title={'Upload a new file'}>
                <Button>
                  <PlusOutlined />
                </Button>
              </Tooltip>
            </Upload>
          </div>
        }
        trigger="click"
      >
        <Button>
          <PaperClipOutlined /> Attach
        </Button>
      </Popover>
    )
  ) : null;
  const allTemplates = defaultTemplate ? [defaultTemplate, ...(templates || [])] : [...(templates || [])];
  const filteredTemplates = searchText
    ? allTemplates.filter((template: Template) => template.title.toLowerCase().includes(searchText.toLowerCase()))
    : allTemplates;
  const toggleTemplatesModal = e => {
    e?.stopPropagation?.();
    setTemplatesDropdownVisible(value => !value);
  };

  const insertVarMenu = (
    <Menu>
      {insertTexts.map((item, index) => (
        <Menu.Item key={index} onClick={() => insertText(item)}>
          {item.label}
        </Menu.Item>
      ))}
    </Menu>
  );

  const filterTemplates = (value: string) => setSearchText(value);

  const inputTemplate = e => {
    toggleTemplatesModal(e);
    const template = filteredTemplates.find(template => parseInt(template.id) === parseInt(e.key));
    setValue(template.text);
  };

  const templatesDropdown = () => {
    return (
      <Dropdown
        disabled={disabled}
        overlayClassName="message-text-area__templates-dropdown"
        overlay={
          <TemplatesMenu
            filterTemplates={filterTemplates}
            searchText={searchText}
            filteredTemplates={filteredTemplates}
            inputTemplate={inputTemplate}
            createTemplate={createTemplate ? () => createTemplate(value) : null}
            inputMessage={value}
            onClose={() => setTemplatesDropdownVisible(false)}
            className={classnames(className, 'message-text-area__templates-trigger')}
          />
        }
        placement="top"
        open={templatesDropdownVisible}
        trigger={['click']}
      >
        <Button
          className={classnames(className, 'message-text-area__templates-trigger')}
          onClick={e => {
            e.stopPropagation();

            if (activeDropdown && activeDropdown !== setTemplatesDropdownVisible) {
              activeDropdown(false);
            }

            activeDropdown = setTemplatesDropdownVisible;

            setTemplatesDropdownVisible(!templatesDropdownVisible);
          }}
          style={{marginLeft: 5}}
        >
          <IconWrapper>
            <FontAwesomeIcon icon={findIconDefinition({prefix: 'fas', iconName: 'edit'})} />
          </IconWrapper>
          Templates
        </Button>
      </Dropdown>
    );
  };

  return (
    <div style={{display: display ? 'block' : 'none'}} className="textArea">
      <Form.Item
        label={
          label ? (
            <>
              {isAdmin && <AdminFeatureIcon />}
              {label}
            </>
          ) : null
        }
        validateStatus={errors && touched ? 'error' : ''}
      >
        <div className="textAreaContainer">
          <div>
            <Tooltip title={tooltip}>
              <Input.TextArea
                data-testid={dataTestId}
                ref={elementRef}
                name={name}
                placeholder={placeholder}
                value={value}
                onChange={ev => setValue(ev.target.value)}
                className="textAreaInput"
                disabled={disabled}
                count={{
                  max: maxLength,
                }}
                // showCount={!!maxLength}
                // maxLength={maxLength}
              />
            </Tooltip>
            {showWhitelabelSignature ? (
              <div className="textAreaWhitelabelSignature">
                <ConfirmationModal
                  visible={showUpgradeConfirmation}
                  message="By clicking yes, you will remove the signature and upgrade to a paying subscription of $99 per month? Are you sure to proceed?"
                  onOk={() => {
                    onUpgradePlan().then(() => setShowUpgradeConfirmation(false));
                  }}
                  onCancel={() => setShowUpgradeConfirmation(false)}
                />
                <Button
                  size="small"
                  type="primary"
                  onClick={() => setShowUpgradeConfirmation(true)}
                  style={{marginLeft: '5px', fontSize: '.9em'}}
                >
                  Upgrade
                </Button>
              </div>
            ) : null}
          </div>
          <div ref={backdropRef} className="messageTextAreaBackdrop">
            <div ref={highlightsRef} className="messageTextAreaHighlights" />
          </div>
          <div className="textAreaButtonsWrapper">
            {onSuggestMessageTemplate && !disabled && (
              <Tooltip title={'Generate Message with AI'}>
                <Button onClick={onSuggestMessageTemplate} className="textAreaAI" icon={<AiIcon />}>
                  Ask AI
                </Button>
              </Tooltip>
            )}
            {templates && templatesDropdown()}
            {insertTexts?.length > 0 && (
              <Dropdown disabled={disabled} overlay={insertVarMenu} placement="bottomLeft">
                <Button>
                  <PlusOutlined /> Insert variable
                </Button>
              </Dropdown>
            )}
            {attachmentElement}
          </div>
        </div>
      </Form.Item>
      <div className="textAreaFooter">
        <ErrorMessage name={name} className="textAreaButtonsError" component="div" />
        {!!maxLength && (
          <div className="textAreaCounter">
            <MessageLengthCount text={value} maxLength={maxLength} />
            {client?.signature_enabled && <InfoIcon message={STRINGS.info.signature_invite_length} />}
          </div>
        )}
      </div>
      <div style={{marginBottom: 5}}>{additionalData}</div>
    </div>
  );
};
